import React, { useEffect, useRef } from 'react';
import './chat.css';

const MAX_INPUT_HEIGHT = 128;

/*
 * Auto-growing composer. Enter sends, Shift+Enter inserts a newline.
 * The textarea stays enabled while a send is in flight — only the send
 * button locks — so a failed send never eats the draft.
 */
const MessageComposer = ({
    value,
    onChange,
    onSend,
    placeholder,
    disabled = false,
    sending = false,
    error = null,
    onRetry,
    onDismissError,
    tourId,
}) => {
    const inputRef = useRef(null);

    // Resize on every value change so programmatic updates (e.g. a restored
    // failed draft) size correctly, not just keystrokes.
    useEffect(() => {
        const el = inputRef.current;
        if (!el) return;
        el.style.height = 'auto';
        // scrollHeight excludes the borders that border-box height must cover.
        const borders = el.offsetHeight - el.clientHeight;
        const grownHeight = el.scrollHeight + borders;
        el.style.height = `${Math.min(grownHeight, MAX_INPUT_HEIGHT)}px`;
        // Scrolling is only enabled at the height ceiling. Below it the input
        // always fits its content, but subpixel rounding (browser zoom,
        // display scaling) can report scrollHeight 1px over clientHeight and
        // summon a phantom scrollbar — overflow stays hidden instead.
        el.style.overflowY = grownHeight > MAX_INPUT_HEIGHT ? 'auto' : 'hidden';
    }, [value]);

    const handleSubmit = (e) => {
        e.preventDefault();
        if (!value.trim() || sending || disabled) return;
        onSend();
    };

    return (
        <div className="wt-composer" data-tour={tourId}>
            {error && (
                <div className="wt-send-error" role="alert">
                    <span className="wt-send-error-text">{error}</span>
                    {onRetry && (
                        <button type="button" className="wt-send-error-retry" onClick={onRetry}>
                            Try again
                        </button>
                    )}
                    {onDismissError && (
                        <button
                            type="button"
                            className="wt-send-error-dismiss"
                            onClick={onDismissError}
                            aria-label="Dismiss"
                        >
                            ×
                        </button>
                    )}
                </div>
            )}
            <form className="wt-composer-form" onSubmit={handleSubmit}>
                <textarea
                    ref={inputRef}
                    className="wt-composer-input"
                    value={value}
                    onChange={(e) => onChange(e.target.value)}
                    onKeyDown={(e) => {
                        if (e.key === 'Enter' && !e.shiftKey) {
                            e.preventDefault();
                            handleSubmit(e);
                        }
                    }}
                    placeholder={placeholder}
                    rows={1}
                    disabled={disabled}
                />
                <button
                    type="submit"
                    className="wt-composer-send"
                    disabled={!value.trim() || sending || disabled}
                    aria-label="Send message"
                    title="Send message"
                >
                    {sending ? (
                        <span className="wt-composer-spinner" />
                    ) : (
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M12 19V5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                            <path d="M5 12l7-7 7 7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                    )}
                </button>
            </form>
        </div>
    );
};

export default MessageComposer;
